import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { FEATURE_OVERLAYS, POI_TYPES, setLayerVisibility } from '@/components/map/utils/mapLayers';
import type { EntityToggleState, FeatureOverlayState, PoiTypeState } from '@/components/map/utils/mapLayers';
import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import type { SemanticZoomLevel } from '@/components/map/utils/semanticZoom';
import { isLayerVisibleAtSemanticLevel } from '@/components/map/utils/semanticZoomLayers';

function applyFeatureOverlayVisibility(
    mlMap: maplibregl.Map,
    featureOverlays: FeatureOverlayState,
    semanticZoomLevel: SemanticZoomLevel,
): void {
    for (const def of FEATURE_OVERLAYS) {
        for (const id of def.mapLayerIds) {
            setLayerVisibility(
                mlMap,
                id,
                featureOverlays[def.key] && isLayerVisibleAtSemanticLevel(id, semanticZoomLevel),
            );
        }
    }
}

// Source-level filtering (filterByEntityToggles in
// useEntitiesLayer) ensures cluster counts and individual icons reflect only enabled
// types/families. This function only needs to toggle layer visibility — the setFilter
// approach is not used.
//
// In entity mode the driver is the two ENTITY toggles, never the legacy per-status
// poiTypes: an entity-era URL carries no legacy status tokens, so the legacy state
// deserialises to all-off there — reading it here would blank every POI/cluster layer
// (sources full, layers visibility:none) with nothing in the Filters or Layers panels
// able to bring them back.
function applyPoiVisibility(
    mlMap: maplibregl.Map,
    poiTypes: PoiTypeState,
    semanticZoomLevel: SemanticZoomLevel,
    entityToggles?: EntityToggleState,
): void {
    const anyEnabled = entityToggles
        ? entityToggles.pois || entityToggles.leaks
        : POI_TYPES.some((d) => poiTypes[d.key]);
    // The viewport POI layers (District Meter Area band inward) and the network cluster
    // layers (Network Overview / Area of Interest bands) have different semantic bands,
    // so each layer's own band is checked rather than sharing one result. The Network
    // Explorer's cell bubbles are its wide end and follow the same toggles and band; they
    // are simply unregistered (a no-op here) outside the explorer.
    const poiLayerIds = [
        ORBIT_LAYER_IDS.pois,
        ORBIT_LAYER_IDS.leaks,
        ORBIT_LAYER_IDS.entityFanBase,
        ORBIT_LAYER_IDS.poiClusters,
        ORBIT_LAYER_IDS.poiClusterCount,
        ORBIT_LAYER_IDS.poiNetworkClusters,
        ORBIT_LAYER_IDS.poiNetworkClusterCount,
        ORBIT_LAYER_IDS.poiNetworkUnclustered,
        ORBIT_LAYER_IDS.explorerClusters,
        ORBIT_LAYER_IDS.explorerClusterCount,
    ];
    for (const id of poiLayerIds) {
        setLayerVisibility(mlMap, id, anyEnabled && isLayerVisibleAtSemanticLevel(id, semanticZoomLevel));
    }
}

// Applies visibility for layers that are controlled by semantic zoom only (no user toggle).
// networkBoundary has no hook yet — setLayerVisibility is a no-op for unregistered layers.
function applySemanticOnlyLayerVisibility(mlMap: maplibregl.Map, semanticZoomLevel: SemanticZoomLevel): void {
    setLayerVisibility(
        mlMap,
        ORBIT_LAYER_IDS.networkBoundary,
        isLayerVisibleAtSemanticLevel(ORBIT_LAYER_IDS.networkBoundary, semanticZoomLevel),
    );
}

export function useMapLayerVisibility(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    featureOverlays: FeatureOverlayState,
    poiTypes: PoiTypeState,
    semanticZoomLevel: SemanticZoomLevel,
    /** Present in entity mode: the POI-family layers follow these, not the legacy poiTypes. */
    entityToggles?: EntityToggleState,
): void {
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const applyAll = () => {
            applyFeatureOverlayVisibility(mlMap, featureOverlays, semanticZoomLevel);
            applyPoiVisibility(mlMap, poiTypes, semanticZoomLevel, entityToggles);
            applySemanticOnlyLayerVisibility(mlMap, semanticZoomLevel);
        };

        applyAll();
        mlMap.on('style.load', applyAll);
        return () => {
            mlMap.off('style.load', applyAll);
        };
    }, [featureOverlays, poiTypes, entityToggles, isMapLoaded, mapRef, semanticZoomLevel]);
}
